import React, { useEffect, useState } from "react";
import { useForm, useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import Head from "../../../layout/head/Head";
import {
  Table,
  Collapse as AntCollapse,
  TableColumnsType,
  Descriptions,
  Modal,
  Timeline,
  Popover,
  Empty,
} from "antd";
import { Row, Col, Button, Badge, ModalBody, Spinner } from "reactstrap";
import dayjs from "dayjs";
import { PaperClipOutlined } from "@ant-design/icons";
import Swal from "sweetalert2";
import { useNavigate } from "react-router";
import QuestionerApprovalDrawer from "./QuestionerApprovalDrawer";
import tenderApis from "../../../api/tender/tenderApis";
import sitesApis from "../../../api/master/sites";
import type { CreateTenderRequest, UpdateTenderRequest } from "../../../api/types/tender";

const { Panel } = AntCollapse;
const Preview: React.FC = (props: any) => {
  const navigate = useNavigate();
  const { control, handleSubmit } = useForm();
  const [sm, setSm] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);
  const { watch, register } = useFormContext();
  const previousFormData = watch();
  const [generalInfoTab, setGeneralInfoTab] = useState("1"); // For General Information panel
  const [questionerTab, setQuestionerTab] = useState(
    (previousFormData?.questionerSections || []).find(
      (s: any) => (parseFloat(s?.sectionWeightage) || 0) > 0
    )?.id || ""
  ); // For Questioner panel
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [disableSubmitDraft, setDisableSubmitDraft] = useState(false);
  const [columns, setColumns] = useState<TableColumnsType<any>>([]);
  const [instructionsVisible, setInstructionsVisible] = useState(false);
  const [itemsCurrentPage, setItemsCurrentPage] = useState(1);
  const [itemsPageSize, setItemsPageSize] = useState(10);
  const [termsVisible, setTermsVisible] = useState(false);
  const [publishApprovals, setPublishApprovals] = useState<{
    open: boolean;
    data: any[];
  }>({
    open: false,
    data: [],
  });
  const [evaluationApprovals, setEvaluationApprovals] = useState<{
    open: boolean;
    data: any[];
  }>({
    open: false,
    data: [],
  });

  const formSiteId = Number(previousFormData.siteId || previousFormData.projectId) || 0;
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const siteName =
    (currentUser?.siteAccessibility || []).find(
      (item: any) => String(item.siteID) === String(previousFormData.siteId)
    )?.siteName || "";
  const [siteSegmentLevel, setSiteSegmentLevel] = useState<string | null>(null);

  useEffect(() => {
    if (!formSiteId) {
      setSiteSegmentLevel(null);
      return;
    }
    (async () => {
      try {
        const { data, status } = await sitesApis.fetchSegmentLevelBySiteId(formSiteId);
        if (status && data) {
          setSiteSegmentLevel(typeof data === "string" ? data : data.segmentLevel || data.segment || null);
        } else {
          setSiteSegmentLevel(null);
        }
      } catch {
        setSiteSegmentLevel(null);
      }
    })();
  }, [formSiteId]);

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, []);

  const [materialColumns, setMaterialColumns] = useState<TableColumnsType<any>>(
    [
      {
        title: "Category",
        dataIndex: "materialCategoryName",
        key: "materialCategoryName",
        width: 250,
        fixed: "left",
      },
      {
        title: "Material Name",
        dataIndex: "materialLabel",
        key: "materialLabel",
        width: 220,
        fixed: "left",
      },
      {
        title: "Material Usage",
        dataIndex: "materialUsage",
        key: "materialUsage",
        width: 200,
      },
      {
        title: "QYT",
        dataIndex: "quantity",
        key: "quantity",
        width: 150,
        render: (text, record) => {
          return (
            <span>
              {text} {record?.uomLabel}
            </span>
          );
        },
      },
      {
        title: "Installation Required",
        dataIndex: "installationRequired",
        key: "installationRequired",
        width: 150,
        render: (value: any) => (value ? "Yes" : "No"),
      },
      {
        title: "Tower / Block",
        dataIndex: "towerBlockVillaLabel",
        key: "towerBlockVillaLabel",
        width: 150,
        render: (text: any) => text || "-",
      },
      {
        title: "Floor",
        dataIndex: "floorLabel",
        key: "floorLabel",
        width: 100,
        render: (text: any) => text || "-",
      },
      {
        title: "Flat",
        dataIndex: "flatLabel",
        key: "flatLabel",
        width: 100,
        render: (text: any) => text || "-",
      },
      {
        title: "NEED BY DATE",
        dataIndex: "needByDate",
        key: "needByDate",
        width: 150,
        render: (text, record) => {
          return <span>{dayjs(text).format("DD-MM-YYYY")}</span>;
        },
      },
    ]
  );

  const [Workcolumns, setWorkColumns] = useState<TableColumnsType<any>>([
    {
      title: "#",
      dataIndex: "key",
      key: "key",
      width: 60,
      fixed: "left",
      render: (_text: any, _record: any, index: number) => {
        return <span>{(itemsCurrentPage - 1) * itemsPageSize + index + 1}</span>;
      },
    },
    {
      title: "Task Title",
      dataIndex: "taskTitle",
      key: "taskTitle",
      width: 250,
      fixed: "left",
    },
    {
      title: "Task Usage",
      dataIndex: "taskUsage",
      key: "taskUsage",
      width: 200,
    },
    {
      title: "Work Category",
      dataIndex: "workCategoryName",
      key: "workCategoryName",
      width: 200,
    },
    {
      title: "QTY",
      dataIndex: "quantity",
      key: "quantity",
      width: 120,
      render: (text: any, record: any) => {
        return (
          <span>
            {text} {record?.uomName}
          </span>
        );
      },
    },
    {
      title: "Installation Required",
      dataIndex: "installationRequired",
      key: "installationRequired",
      width: 150,
      render: (value: any) => (value ? "Yes" : "No"),
    },
  ]);

  const [Questionaircolumns, setQuestionairColumns] = useState<
    TableColumnsType<any>
  >([
    {
      title: "Question",
      dataIndex: "questioner",
      key: "questioner",
    },
    {
      title: "Type",
      dataIndex: "questionType",
      key: "questionType",
    },
    {
      title: "Weightage",
      dataIndex: "questionWeightage",
      key: "questionWeightage",
      render: (text: any) => {
        const val = String(text || "").replace(/[^0-9.]/g, "");
        return val ? `${val} %` : "";
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
  ]);

  useEffect(() => {
    if (previousFormData.tenderFor === "Material") {
      setColumns(materialColumns);
    } else {
      setColumns(Workcolumns);
    }
  }, [previousFormData]);

  const buildTenderRequest = (isDraft: boolean): { request: CreateTenderRequest; files: File[] } => {
    const formData = previousFormData;

    // Map tenderFor value
    let tenderFor: "Material" | "Service" | "Work" = "Material";
    if (formData.tenderFor === "Service / Works") {
      tenderFor = "Service";
    } else if (formData.tenderFor === "Material") {
      tenderFor = "Material";
    }

    // Build settings
    const settings = {
      isLineLevelDelivery: formData.deliveryLineLevel === "true" || formData.deliveryLineLevel === true,
      approvalRequiredToPublish:
        formData.approvalToPublish === "true" || formData.approvalToPublish === true || publishApprovals.data.length > 0,
      questionerForEligibility: formData.questionerForEligibility === "true" || formData.questionerForEligibility === true,
      approvalRequiredForEvaluation:
        formData.approvalForEvaluation === "true" || formData.approvalForEvaluation === true || evaluationApprovals.data.length > 0,
      documentsFromSuppliersOrContractors: formData.documnetsRequired === "true" || formData.documnetsRequired === true,
    };

    // Build materials array
    // Note: form stores material ID in "materialName" field (from Select value)
    // and display name in "materialLabel" field
    const materials = tenderFor === "Material"
      ? (formData.items || []).map((item: any) => ({
          ...(item.tenderMaterialId ? { tenderMaterialId: Number(item.tenderMaterialId) } : {}),
          materialId: Number(item.materialName) || item.materialId || 0,
          materialName: item.materialLabel || "",
          quantity: Number(item.quantity) || 0,
          uomId: Number(item.uomId) || 0,
          uomName: item.uomLabel || "",
          description: item.description || "",
          deliveryAddressId: item.deliveryAddressId,
          needByDate: item.needByDate
            ? dayjs(item.needByDate).format("YYYY-MM-DDTHH:mm:ss")
            : "",
          usage: item.materialUsage || "",
          isInstallationRequired: item.installationRequired || false,
          ...(item.towerBlockVillaId ? { siteStructureUnitId: Number(item.towerBlockVillaId) } : {}),
          ...(item.floorNo ? { floorNo: item.floorNo } : {}),
          ...(item.flatId ? { siteStructureSubUnitId: Number(item.flatId) } : {}),
          ...(item.locationId ? { locationId: Number(item.locationId) } : {}),
        }))
      : [];

    // Build tasks array
    const tasks = tenderFor !== "Material"
      ? (formData.items || []).map((item: any) => ({
          ...(item.tenderTaskId ? { tenderTaskId: Number(item.tenderTaskId) } : {}),
          workType: item.workType || "Open",
          workCategoryId: Number(item.workCategoryId) || 0,
          taskTitle: item.taskTitle || "",
          description: item.description || "",
          quantity: Number(item.quantity) || 0,
          uomId: Number(item.uomId) || 0,
          usage: item.taskUsage || "",
          isInstallationRequired: item.installationRequired || false,
          ...(item.towerBlockVillaId ? { siteStructureUnitId: Number(item.towerBlockVillaId) } : {}),
          ...(item.floorNo ? { floorNo: item.floorNo } : {}),
          ...(item.flatId ? { siteStructureSubUnitId: Number(item.flatId) } : {}),
          ...(item.locationId ? { locationId: Number(item.locationId) } : {}),
        }))
      : [];

    // Build questionnaire sections (exclude sections with 0 weightage)
    const questionaries = (formData.questionerSections || []).filter(
      (section: any) => (parseFloat(section?.sectionWeightage || section?.weightage || "0") || 0) > 0
    ).map((section: any) => {
      const sectionWeightage = section.sectionWeightage || section.weightage || section.totalSetionWeight || "0";
      return {
        ...(section.tenderSectionId ? { tenderSectionId: Number(section.tenderSectionId) } : {}),
        sectionName: section.name,
        weightage: Number(String(sectionWeightage).replace(/[^0-9.]/g, "")) || 0,
        approvals: section.approvals || [],
        evaluators: (section.evaluators || []).map((id: any) => ({ userId: Number(id) })),
        questions: (section.questions || []).map((q: any) => ({
          ...(q.tenderQuestionId ? { tenderQuestionId: Number(q.tenderQuestionId) } : {}),
          question: q.questioner || q.question || "",
          type: q.questionType || q.type || "Short Text",
          options: q.choiceOptions || q.options || [],
          weightage: Number(String(q.questionWeightage || "0").replace(/[^0-9.]/g, "")) || 0,
          description: q.description || "",
        })),
      };
    });

    // Extract files (order is 0-based to match file position)
    const files: File[] = [];
    const filesData: any[] = [];

    // Extract files from material/task line items
    (formData.items || []).forEach((item: any) => {
      if (item.file && item.file[0] instanceof File) {
        files.push(item.file[0]);
        const fileEntry: any = {
          order: files.length - 1,
          module: "TMS",
          moduleFileType: tenderFor === "Material" ? "MaterialDocument" : "TaskDocument",
        };
        // Add materialId for material documents
        if (tenderFor === "Material") {
          fileEntry.materialId = Number(item.materialName) || item.materialId || 0;
        }
        // Conditionally add taskTitle or materialIdDeliveryAddressId based on lineLevelDelivery
        if (settings.isLineLevelDelivery) {
          fileEntry.materialIdDeliveryAddressId = String(item.deliveryAddressId || "");
        } else {
          fileEntry.taskTitle = item.materialLabel || item.categoryLabel || item.taskTitle || "";
        }
        filesData.push(fileEntry);
      }
    });

    // Extract Instructions as HTML file
    if (formData.instructions) {
      const instructionsBlob = new Blob([formData.instructions], { type: "text/html" });
      const instructionsFile = new File([instructionsBlob], "Instructions.html", { type: "text/html" });
      files.push(instructionsFile);
      filesData.push({
        order: files.length - 1,
        module: "TMS",
        moduleFileType: "Instructions",
      });
    }

    // Extract Terms and Conditions as HTML file
    if (formData.termsAndConditions) {
      const termsBlob = new Blob([formData.termsAndConditions], { type: "text/html" });
      const termsFile = new File([termsBlob], "TermsAndConditions.html", { type: "text/html" });
      files.push(termsFile);
      filesData.push({
        order: files.length - 1,
        module: "TMS",
        moduleFileType: "TermsAndConditions",
      });
    }

    // Extract files from Others step attachments
    if (formData.attachments && formData.attachments.length > 0) {
      formData.attachments.forEach((attachment: any) => {
        const file = attachment.originFileObj || attachment;
        if (file instanceof File) {
          files.push(file);
          filesData.push({
            order: files.length - 1,
            module: "TMS",
            moduleFileType: "TenderDocument",
          });
        }
      });
    }

    // Build supplier/contractor objects for restricted tenders
    const suppliers = (formData.supplierIds || []).map((id: any) => ({ supplierId: Number(id) }));
    const contractors = (formData.contractorIds || []).map((id: any) => ({ contractorId: Number(id) }));

    const request: CreateTenderRequest = {
      tenderNumber: formData.tenderNumber || undefined,
      tenderTitle: formData.tenderTitle || "",
      tenderType: formData.tenderType || "Open",
      description: formData.description || "",
      isDraft,
      tenderFor,
      lastSubmissionDate: formData.lastSubmissionDate
        ? dayjs(formData.lastSubmissionDate).format("YYYY-MM-DDTHH:mm:ss")
        : "",
      siteId: Number(formData.siteId || formData.projectId) || 0,
      segmentLevel: (siteSegmentLevel as "Tower" | "Villa" | "Floor" | "Flat") || undefined,
      lastDateForExpressingInterest: formData.lastDateForExpressingInterest
        ? dayjs(formData.lastDateForExpressingInterest).format("YYYY-MM-DDTHH:mm:ss")
        : undefined,
      evaluationStartDate: formData.evaluationStartDate
        ? dayjs(formData.evaluationStartDate).format("YYYY-MM-DDTHH:mm:ss")
        : undefined,
      sealedBidSubmissionDate: formData.sealedBidSubmissionDate
        ? dayjs(formData.sealedBidSubmissionDate).format("YYYY-MM-DDTHH:mm:ss")
        : undefined,
      sealedBidPostalAddress: formData.sealedBidPostalAddress || undefined,
      suppliers: suppliers.length > 0 ? suppliers : undefined,
      contractors: contractors.length > 0 ? contractors : undefined,
      settings,
      materials,
      tasks,
      questionaries,
      publishApprovals: publishApprovals.data || [],
      evaluationApprovals: evaluationApprovals.data || [],
      filesData,
    };

    return { request, files };
  };

  const buildUpdateRequest = (isDraft: boolean): { request: UpdateTenderRequest; files: File[] } => {
    const formData = previousFormData;
    const { request: createReq, files } = buildTenderRequest(true);

    const currentMaterialIds = new Set(
      (createReq.materials || [])
        .map((m: any) => m.tenderMaterialId)
        .filter(Boolean)
        .map(Number)
    );
    const deletedMaterials = (formData._originalMaterialIds || []).filter(
      (id: any) => !currentMaterialIds.has(Number(id))
    );

    const currentTaskIds = new Set(
      (createReq.tasks || [])
        .map((t: any) => t.tenderTaskId)
        .filter(Boolean)
        .map(Number)
    );
    const deletedTasks = (formData._originalTaskIds || []).filter(
      (id: any) => !currentTaskIds.has(Number(id))
    );

    const updateQuestionaries = (createReq.questionaries || []).map(
      (section: any) => {
        const sectionId = section.tenderSectionId;
        const originalQuestionIds: number[] = sectionId
          ? formData._originalQuestionIdsBySection?.[sectionId] || []
          : [];
        const currentQuestionIds = new Set(
          (section.questions || [])
            .map((q: any) => q.tenderQuestionId)
            .filter(Boolean)
            .map(Number)
        );
        const deletedQuestions = originalQuestionIds.filter(
          (id: number) => !currentQuestionIds.has(Number(id))
        );
        const originalEvaluatorIds: number[] = sectionId
          ? formData._originalEvaluatorIdsBySection?.[sectionId] || []
          : [];
        return {
          ...section,
          deletedQuestions,
          deletedQuestionaryApprovals: [],
          deletedQuestionaryEvaluators: originalEvaluatorIds,
        };
      }
    );

    const currentSectionIds = new Set(
      (createReq.questionaries || [])
        .map((s: any) => s.tenderSectionId)
        .filter(Boolean)
        .map(Number)
    );
    const deletedQuestionaries = (formData._originalSectionIds || []).filter(
      (id: any) => !currentSectionIds.has(Number(id))
    );

    const existingDocIds = new Set(
      (formData.attachments || [])
        .map((a: any) => a.docId)
        .filter(Boolean)
        .map(Number)
    );
    const deletedDocIds: number[] = (formData._originalDocumentIds || []).filter(
      (id: any) =>
        !existingDocIds.has(Number(id)) &&
        id !== formData._instructionsDocId &&
        id !== formData._termsDocId
    );
    if (formData.instructions && formData._instructionsDocId) {
      deletedDocIds.push(formData._instructionsDocId);
    }
    if (formData.termsAndConditions && formData._termsDocId) {
      deletedDocIds.push(formData._termsDocId);
    }

    const supplierMap: Record<string, number> = formData._originalSupplierMap || {};
    const currentSupplierIds = (formData.supplierIds || []).map((id: any) => Number(id));
    const suppliers = currentSupplierIds.map((id: number) => {
      const existingId = supplierMap[String(id)];
      return {
        ...(existingId ? { tenderSupplierId: Number(existingId) } : {}),
        supplierId: id,
      };
    });
    const currentSupplierIdSet = new Set(currentSupplierIds);
    const deletedSuppliers = Object.entries(supplierMap)
      .filter(([supplierId]) => !currentSupplierIdSet.has(Number(supplierId)))
      .map(([, tenderSupplierId]) => Number(tenderSupplierId));

    const contractorMap: Record<string, number> = formData._originalContractorMap || {};
    const currentContractorIds = (formData.contractorIds || []).map((id: any) => Number(id));
    const contractors = currentContractorIds.map((id: number) => {
      const existingId = contractorMap[String(id)];
      return {
        ...(existingId ? { tenderContractorId: Number(existingId) } : {}),
        contractorId: id,
      };
    });
    const currentContractorIdSet = new Set(currentContractorIds);
    const deletedContractors = Object.entries(contractorMap)
      .filter(([contractorId]) => !currentContractorIdSet.has(Number(contractorId)))
      .map(([, tenderContractorId]) => Number(tenderContractorId));

    const updateRequest: UpdateTenderRequest = {
      tenderId: Number(formData._draftTenderId),
      isDraft,
      tenderTitle: createReq.tenderTitle,
      tenderType: createReq.tenderType,
      description: createReq.description,
      tenderFor: createReq.tenderFor,
      lastSubmissionDate: createReq.lastSubmissionDate,
      siteId: createReq.siteId,
      lastDateForExpressingInterest: createReq.lastDateForExpressingInterest,
      evaluationStartDate: createReq.evaluationStartDate,
      sealedBidSubmissionDate: createReq.sealedBidSubmissionDate,
      sealedBidPostalAddress: createReq.sealedBidPostalAddress,
      settings: createReq.settings,
      materials: createReq.materials,
      deletedMaterials,
      tasks: createReq.tasks,
      deletedTasks,
      questionaries: updateQuestionaries,
      deletedQuestionaries,
      publishApprovals: createReq.publishApprovals || [],
      deletedPublishApprovals: [],
      evaluationApprovals: createReq.evaluationApprovals || [],
      deletedEvaluationApprovals: [],
      suppliers,
      deletedSuppliers,
      contractors,
      deletedContractors,
      filesData: createReq.filesData,
      deletedDocIds,
    };

    return { request: updateRequest, files };
  };

  const isEditMode = !!previousFormData._editMode && !!previousFormData._draftTenderId;

  const onSubmit = (data: any) => {
    setIsSubmitting(true);
    Swal.fire({
      title: "Are you sure?",
      text: isEditMode ? "You want to Update Tender!" : "You want to Create Tender!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: isEditMode ? "Yes, update!" : "Yes, submit!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          let response: any;
          if (isEditMode) {
            const { request, files } = buildUpdateRequest(false);
            response = await tenderApis.updateTender(request, files);
          } else {
            const { request, files } = buildTenderRequest(false);
            response = await tenderApis.createTender(request, files);
          }
          if (response?.status || response?.data) {
            Swal.fire(
              isEditMode ? "Updated!" : "Submitted!",
              isEditMode
                ? "Your Tender has been Updated."
                : "Your Tender has been Created.",
              "success"
            );
            navigate("/tender/tender-list");
          } else {
            Swal.fire("Error", response?.message || "Failed to submit tender", "error");
          }
        } catch (error: any) {
          console.error("Submit tender error:", error);
          Swal.fire("Error", error?.message || "Failed to submit tender", "error");
        } finally {
          setIsSubmitting(false);
        }
      } else {
        setIsSubmitting(false);
      }
    });
  };

  const saveDraft = () => {
    setDisableSubmitDraft(true);
    Swal.fire({
      title: "Are you sure?",
      text: "You want to Save as Draft!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, save!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          let response: any;
          if (isEditMode) {
            const { request, files } = buildUpdateRequest(true);
            response = await tenderApis.updateTender(request, files);
          } else {
            const { request, files } = buildTenderRequest(true);
            response = await tenderApis.createTender(request, files);
          }
          if (response?.status || response?.data) {
            Swal.fire("Saved!", "Your Tender has been Saved as Draft.", "success");
            navigate("/tender/tender-list");
          } else {
            Swal.fire("Error", response?.message || "Failed to save draft", "error");
          }
        } catch (error: any) {
          console.error("Save draft error:", error);
          Swal.fire("Error", error?.message || "Failed to save draft", "error");
        } finally {
          setDisableSubmitDraft(false);
        }
      } else {
        setDisableSubmitDraft(false);
      }
    });
  };
  return (
    <>
      <Head title="New - Tender" />
      <div className="content clearfix">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Row className="mb-3">
            <div className="col-md-6">
              <h5 className="title">Preview</h5>
            </div>
            <div className="col-md-6 text-end">
              <a
                href="#"
                className="btn btn-sm btn-outline-primary"
                onClick={(e) => {
                  e.preventDefault();
                  setPublishApprovals((pre) => ({
                    ...pre,
                    open: true,
                  }));
                }}
              >
                Set Publish Approvals
              </a>
              <a
                style={{ marginLeft: "2%" }}
                href="#"
                className="btn btn-sm btn-outline-info"
                onClick={(e) => {
                  e.preventDefault();
                  setEvaluationApprovals((pre) => ({
                    ...pre,
                    open: true,
                  }));
                }}
              >
                Set Evaluate Approvals
              </a>
            </div>
          </Row>
          <AntCollapse defaultActiveKey={["1", "2", "3", "4", "5"]}>
            <Panel
              header={
                <div className="d-flex align-items-center justify-content-between">
                  <>General Information</>
                </div>
              }
              key="1"
            >
              <ul className="nav nav-tabs nav-tabs-card nav-tabs-xs w-100 p-0 m-0">
                <li className="nav-item" onClick={() => setGeneralInfoTab("1")}>
                  <a
                    onClick={(ev) => {
                      ev.preventDefault();
                    }}
                    className={`nav-link${
                      generalInfoTab === "1" ? " active" : ""
                    }`}
                    href="#year"
                  >
                    General Info
                  </a>
                </li>
                <li className="nav-item" onClick={() => setGeneralInfoTab("2")}>
                  <a
                    href="#overview"
                    onClick={(ev) => {
                      ev.preventDefault();
                    }}
                    className={`nav-link${
                      generalInfoTab === "2" ? " active" : ""
                    }`}
                  >
                    Tender Settings
                  </a>
                </li>
              </ul>

              <div className="tab-content mt-0">
                {generalInfoTab === "1" && (
                  <div
                    className={`tab-pane${generalInfoTab === "1" && " active"}`}
                  >
                    <Col className="justify-start w-100  flex-wrap p-3">
                      <div className="w-50 border-end ">
                        <div className="data-col align-center pt-1 ">
                          <div className="data-label w-40">Tender #</div>
                          <div className="data-value w-60 ">
                            {previousFormData.tenderNumber}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1 ">
                          <div className="data-label w-40">Tender Type</div>
                          <div className="data-value w-60 ">
                            {previousFormData.tenderType}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1 ">
                          <div className="data-label w-40">Tender For</div>
                          <div className="data-value w-60 ">
                            {previousFormData.tenderFor}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-40">Description</div>
                          <div className="data-value w-60">
                            <Popover
                              content={
                                <div>
                                  <p>{previousFormData?.description}</p>
                                </div>
                              }
                              title={"Description"}
                            >
                              {previousFormData?.description?.length > 30 ? (
                                <span
                                  style={{
                                    color: "#2263b3",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                  }}
                                >
                                  {previousFormData?.description?.substring(
                                    0,
                                    30
                                  )}
                                  ...
                                </span>
                              ) : (
                                previousFormData?.description || "--"
                              )}
                            </Popover>
                          </div>
                        </div>
                        {previousFormData.tenderFor === "Material" && (
                          <div className="data-col align-center pt-1 ">
                            <div className="data-label w-40">Site</div>
                            <div className="data-value w-60 ">
                              {siteName
                                ? `${siteName} (${previousFormData.siteId})`
                                : previousFormData.siteId || "--"}
                            </div>
                          </div>
                        )}
                        {previousFormData.tenderFor !== "Material" && (
                          <div className="data-col align-center pt-1 ">
                            <div className="data-label w-40">Project</div>
                            <div className="data-value w-60 ">
                              {previousFormData.project}
                            </div>
                          </div>
                        )}
                        {previousFormData.tenderType === "Restricted" &&
                          previousFormData.tenderFor === "Material" && (
                          <div className="data-col align-center pt-1 ">
                            <div className="data-label w-40">Suppliers</div>
                            <div className="data-value w-60 ">
                              {previousFormData.supplierIds?.length > 0
                                ? `${previousFormData.supplierIds.length} selected`
                                : "N/A"}
                            </div>
                          </div>
                        )}
                        {previousFormData.tenderType === "Restricted" &&
                          previousFormData.tenderFor === "Service / Works" && (
                          <div className="data-col align-center pt-1 ">
                            <div className="data-label w-40">Contractors</div>
                            <div className="data-value w-60 ">
                              {previousFormData.contractorIds?.length > 0
                                ? `${previousFormData.contractorIds.length} selected`
                                : "N/A"}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="w-50 paddingLeft10 ">
                        <div className="data-col align-center pt-1 ">
                          <div className="data-label w-50">
                            Last Submission Date
                          </div>
                          <div className="data-value w-50" style={{ whiteSpace: "nowrap" }}>
                            {dayjs(previousFormData?.lastSubmissionDate).format(
                              "DD-MMM-YYYY hh:mm A"
                            )}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1 ">
                          <div className="data-label w-50">
                            Last Date For Expressing Interest
                          </div>
                          <div className="data-value w-50" style={{ whiteSpace: "nowrap" }}>
                            {dayjs(
                              previousFormData?.lastDateForExpressingInterest
                            ).format("DD-MMM-YYYY hh:mm A")}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1 ">
                          <div className="data-label w-50">
                            Evaluation Start Date
                          </div>
                          <div className="data-value w-50" style={{ whiteSpace: "nowrap" }}>
                            {previousFormData?.evaluationStartDate
                              ? dayjs(
                                  previousFormData?.evaluationStartDate
                                ).format("DD-MMM-YYYY hh:mm A")
                              : "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1 ">
                          <div className="data-label w-50">
                            Sealed Bid Submission Date
                          </div>
                          <div className="data-value w-50" style={{ whiteSpace: "nowrap" }}>
                            {previousFormData?.sealedBidSubmissionDate
                              ? dayjs(
                                  previousFormData?.sealedBidSubmissionDate
                                ).format("DD-MMM-YYYY hh:mm A")
                              : "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1 ">
                          <div className="data-label w-50">
                            Sealed Bid Postal Address
                          </div>
                          <div className="data-value w-50">
                            {previousFormData?.sealedBidPostalAddress || "--"}
                          </div>
                        </div>
                      </div>
                    </Col>
                  </div>
                )}
                {generalInfoTab === "2" && (
                  <div
                    className={`tab-pane${generalInfoTab === "2" && " active"}`}
                  >
                    <Col className="justify-start w-100  flex-wrap mt-3 p-3">
                      <div className="w-30 border-end ">
                        <div className="data-col align-center pt-1 ">
                          <div className="data-label w-70">
                            Is the Delivery Line Level ?
                          </div>
                          <div className="data-value w-30 ">{"Yes"}</div>
                        </div>
                        <div className="data-col align-center pt-1 ">
                          <div className="data-label w-70">
                            Approval to Publish
                          </div>
                          <div className="data-value w-30 ">{"Yes"}</div>
                        </div>
                      </div>
                      <div className="w-30 border-end paddingLeft10  ">
                        <div className="data-col align-center pt-1 ">
                          <div className="data-label w-70">
                            Questioner For Eligibility{" "}
                          </div>
                          <div className="data-value w-30 ">{"Yes"}</div>
                        </div>
                        <div className="data-col align-center pt-1 ">
                          <div className="data-label w-70">
                            Approval For Evaluation{" "}
                          </div>
                          <div className="data-value w-30 ">{"Yes"}</div>
                        </div>
                      </div>
                      <div className="w-30  paddingLeft10  ">
                        <div className="data-col align-center pt-1 ">
                          <div className="data-label w-70">
                            Documents From Suppliers / Contractors
                          </div>
                          <div className="data-value w-30 ">{"Yes"}</div>
                        </div>
                      </div>
                    </Col>
                  </div>
                )}
              </div>
            </Panel>

            <Panel
              header={
                previousFormData.tenderFor === "Material"
                  ? "Material Information"
                  : "Work / Service"
              }
              key="2"
            >
              <div className="table-responsive">
                <div
                  style={{
                    width: screenSize - 170 + "px",
                    margin: "auto",
                  }}
                >
                  {columns && (
                    <Table
                      className="customTable"
                      columns={columns}
                      dataSource={previousFormData?.items}
                      scroll={{ x: 1000 }}
                      expandable={{
                        expandedRowRender: (record: any) => (
                          <div className="expandableBody">
                            <div style={{ flex: 1, textAlign: "left" }}>
                              <p style={{ margin: 0 }}>{record.description}</p>
                              {record.file && record.file[0] && (
                                <div>
                                  <PaperClipOutlined /> &nbsp;
                                  <a
                                    href={URL.createObjectURL(record.file[0])}
                                    download={record.file[0]?.name}
                                  >
                                    {record.file[0]?.name}
                                  </a>
                                </div>
                              )}
                            </div>
                            {previousFormData?.deliveryAddressType ===
                              "Line Level" &&
                              record.deliveryAddressId && (
                                <div
                                  className="lineAddress"
                                  style={{ flex: 1 }}
                                >
                                  <h6>Delivery Address</h6>
                                  {/* <ShowAddressPopOver
                                    id={record.deliveryAddressId}
                                  /> */}
                                </div>
                              )}
                          </div>
                        ),
                      }}
                      bordered
                      size="small"
                      pagination={{
                        current: itemsCurrentPage,
                        pageSize: itemsPageSize,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "50", "100"],
                        showTotal: (total, range) =>
                          `${range[0]}-${range[1]} of ${total} items`,
                        onChange: (page, size) => {
                          setItemsCurrentPage(page);
                          setItemsPageSize(size);
                        },
                      }}
                    />
                  )}
                </div>
              </div>
            </Panel>

            <Panel header="Information For Evaluation" key="3">
              <ul className="nav nav-tabs nav-tabs-card nav-tabs-xs w-100 p-0 m-0">
                {previousFormData?.questionerSections
                  .filter((section: any) => (parseFloat(section?.sectionWeightage) || 0) > 0)
                  .map((section: any, index: number) => (
                    <li
                      className="nav-item"
                      key={section.id}
                      onClick={() => setQuestionerTab(section.id)}
                    >
                      <a
                        onClick={(ev) => {
                          ev.preventDefault();
                        }}
                        className={`nav-link${
                          questionerTab === section.id ? " active" : ""
                        }`}
                        href="#"
                      >
                        {section.name}
                      </a>
                    </li>
                  )
                )}
              </ul>
              <div className="tab-content mt-0">
                {previousFormData?.questionerSections
                  .filter((section: any) => (parseFloat(section?.sectionWeightage) || 0) > 0)
                  .map((section: any, sIdx: number) => (
                  <div
                    key={section.id}
                    className={`tab-pane fade${
                      questionerTab === section.id ? " show active" : ""
                    }`}
                  >
                    {questionerTab === section.id && (
                      <div
                        className={`tab-pane${
                          questionerTab === section.id && " active"
                        }`}
                      >
                        <div className="table-responsive mt-2">
                          <div
                            style={{
                              width: screenSize - 170 + "px",
                              margin: "auto",
                            }}
                          >
                            <Row>
                              <div className="data-label w-15">
                                Total Weightage
                              </div>
                              <div className="data-value w-30 ">{section.sectionWeightage || "0"}%</div>

                              <div className="data-label w-15">
                                Information Approvals
                              </div>
                              <div className="data-value w-30 ">
                                {(section.evaluatorDetails || []).length > 0 ? (
                                  <Popover
                                    content={
                                      <div>
                                        {(section.evaluatorDetails || []).map((ev: any, i: number) => (
                                          <div key={i} style={{ padding: "4px 0", fontSize: 14 }}>
                                            {ev.userName}
                                          </div>
                                        ))}
                                      </div>
                                    }
                                    title={<span style={{ color: "#2263b3", fontWeight: 600 }}>Assigned Evaluators</span>}
                                    trigger="hover"
                                  >
                                    <span
                                      style={{
                                        color: "#2263b3",
                                        textDecoration: "underline",
                                        cursor: "pointer",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {(section.evaluatorDetails || []).length} Evaluator(s)
                                    </span>
                                  </Popover>
                                ) : (
                                  <span style={{ color: "#888" }}>--</span>
                                )}
                              </div>
                            </Row>
                            {columns && (
                              <Table
                                className="mt-3"
                                columns={Questionaircolumns}
                                dataSource={section.questions}
                                scroll={{ x: "max-content" }} // Adjust this as necessary
                                expandable={{
                                  expandedRowRender: (record: any) => (
                                    <div className="expandableBody">
                                      <div
                                        style={{ flex: 1, textAlign: "left" }}
                                      >
                                        {record?.choiceOptions &&
                                          record.choiceOptions.length > 0 && (
                                            <>
                                              <p style={{ margin: 0 }}>
                                                Options
                                              </p>
                                              <ul className="list-group mb-3">
                                                {record.choiceOptions.map(
                                                  (
                                                    option: string,
                                                    index: number
                                                  ) => (
                                                    <li
                                                      key={index} // It's good practice to use a key when mapping
                                                      className="list-group-item d-flex justify-content-between align-items-center"
                                                    >
                                                      <span>{option}</span>
                                                    </li>
                                                  )
                                                )}
                                              </ul>
                                            </>
                                          )}
                                      </div>
                                    </div>
                                  ),
                                }}
                                locale={{
                                  emptyText: (
                                    <span
                                      style={{
                                        height: "300px",
                                        color: "#333",
                                        fontSize: "16px",
                                      }}
                                    >
                                      Please click on "Add New Item" to add
                                      question.
                                    </span>
                                  ),
                                }}
                                bordered
                                size="small"
                                pagination={false}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Panel>

            <Panel header="Terms and Conditions / Documents" key="4">
              <div>
                <Descriptions title="" className="custom-description-title">
                  <Descriptions.Item label="Instructions">
                    <span
                      style={{
                        color: "#2263b3",
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                      onClick={() => setInstructionsVisible(true)}
                    >
                      View
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label="Terms and Conditions">
                    <span
                      style={{
                        color: "#2263b3",
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                      onClick={() => setTermsVisible(true)}
                    >
                      View
                    </span>
                  </Descriptions.Item>
                  {previousFormData?.attachments?.length > 0 && (
                    <Descriptions.Item label="">
                      <div>
                        <div className="">
                          Attachments &nbsp;
                          <PaperClipOutlined />
                        </div>
                        <div>
                          {previousFormData.attachments?.map(
                            (attachment: any, index: number) => {
                              return (
                                <div
                                  key={index}
                                  className="w-100 d-flex justify-content-between"
                                >
                                  <div>
                                    <strong>{index + 1}.</strong>
                                    &nbsp;
                                    {attachment.name}
                                    &nbsp; &nbsp; &nbsp; &nbsp;
                                  </div>
                                  <div>
                                    {/* <a
                                  href={URL.createObjectURL(
                                    attachment?.originFileObj
                                  )}
                                  download={attachment.name}
                                  style={{ color: "black", fontSize: "18px" }}
                                >
                                  <DownloadOutlined />
                                </a> */}
                                  </div>
                                </div>
                              );
                            }
                          )}
                        </div>
                      </div>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </div>
            </Panel>
          </AntCollapse>
          <Modal
            title="Instructions"
            open={instructionsVisible}
            footer={null}
            onCancel={() => setInstructionsVisible(false)}
            width="60%"
            styles={{ body: { maxHeight: "70vh", overflowY: "auto" } }}
          >
            {previousFormData?.instructions ? (
              <pre>{previousFormData?.instructions}</pre>
            ) : (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                }}
              >
                <Empty description="No instructions provided for this Tender" />
              </div>
            )}
          </Modal>
          <Modal
            title="Terms and Conditions"
            open={termsVisible}
            footer={null}
            onCancel={() => setTermsVisible(false)}
            width="60%"
            styles={{ body: { maxHeight: "70vh", overflowY: "auto" } }}
          >
            {previousFormData?.termsAndConditions ? (
              <pre>{previousFormData?.termsAndConditions}</pre>
            ) : (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                }}
              >
                <Empty description="No terms and conditions provided for this Tender" />
              </div>
            )}
          </Modal>

          <div className="actions clearfix">
            <ul>
              <li>
                {isSubmitting ? (
                  <Button color="primary" type="button">
                    <Spinner size="sm" color="light" />
                  </Button>
                ) : (
                  <Button color="primary" type="submit">
                    Submit
                  </Button>
                )}
              </li>
              <li>
                <Button
                  color="primary"
                  onClick={() => {
                    props.prev();
                  }}
                  className="btn-dim btn btn-secondary"
                >
                  Previous
                </Button>
              </li>
              <li>
                {disableSubmitDraft ? (
                  <Button color="secondary" type="button">
                    <Spinner size="sm" color="light" />
                  </Button>
                ) : (
                  <Button
                    color="secondary"
                    onClick={() => saveDraft()}
                    className="btn btn-secondary"
                  >
                    Save as Draft
                  </Button>
                )}
              </li>
            </ul>
          </div>
        </form>
        {publishApprovals.open && (
          <QuestionerApprovalDrawer
            open={publishApprovals.open}
            title="Set Publish Approvals"
            siteId={formSiteId}
            moduleId={211}
            onClose={() => {
              setPublishApprovals((pre) => ({
                ...pre,
                open: false,
              }));
            }}
            onSubmit={(data: any) => {
              setPublishApprovals({
                open: false,
                data: data,
              });
            }}
            setApprovalSubmissionStatus={(status: boolean) => {}}
            approvalData={publishApprovals.data}
            onError={(message: string) => {
              setPublishApprovals((pre) => ({ ...pre, open: false }));
              Swal.fire("Approval Info", message, "info");
            }}
          />
        )}
        {evaluationApprovals.open && (
          <QuestionerApprovalDrawer
            open={evaluationApprovals.open}
            title="Set Evaluate Approvals"
            siteId={formSiteId}
            moduleId={211}
            onClose={() => {
              setEvaluationApprovals((pre) => ({
                ...pre,
                open: false,
              }));
            }}
            onSubmit={(data: any) => {
              setEvaluationApprovals({
                open: false,
                data: data,
              });
            }}
            setApprovalSubmissionStatus={(status: boolean) => {}}
            approvalData={evaluationApprovals.data}
            onError={(message: string) => {
              setEvaluationApprovals((pre) => ({ ...pre, open: false }));
              Swal.fire("Approval Info", message, "info");
            }}
          />
        )}
      </div>
    </>
  );
};

export default Preview;
